import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";

import Landing from "@/pages/Landing";
import Intake from "@/pages/Intake";
import Report from "@/pages/Report";
import ClientLogin from "@/pages/ClientLogin";
import ClientDashboard from "@/pages/ClientDashboard";
import Followup from "@/pages/Followup";
import Start from "@/pages/Start";
import AdminLogin from "@/pages/AdminLogin";
import AdminForgotPassword from "@/pages/AdminForgotPassword";
import AdminAcceptInvite from "@/pages/AdminAcceptInvite";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminPractitioners from "@/pages/AdminPractitioners";
import PractitionerDashboard from "@/pages/PractitionerDashboard";
import AdminProAssessment from "@/pages/AdminProAssessment";
import WellnessAssessment from "@/pages/WellnessAssessment";
import Assessment from "@/pages/Assessment";
import AdminWellnessGatewayLeads from "@/pages/AdminWellnessGatewayLeads";
import AdminFunnelInquiries from "@/pages/AdminFunnelInquiries";
import AdminKcisAdaptiveSessions from "@/pages/AdminKcisAdaptiveSessions";
import AdminKcisAdaptiveReporting from "@/pages/AdminKcisAdaptiveReporting";
import AdminKcisMakeOperations from "@/pages/AdminKcisMakeOperations";
import FunnelUniversity from "@/pages/funnels/FunnelUniversity";
import FunnelCoaching from "@/pages/funnels/FunnelCoaching";
import FunnelKcisClinical from "@/pages/funnels/FunnelKcisClinical";
import FunnelComfortCare from "@/pages/funnels/FunnelComfortCare";
import UniversityLanding from "@/pages/university/UniversityLanding";
import UniversityCollege from "@/pages/university/UniversityCollege";
import UniversityAbout from "@/pages/university/UniversityAbout";
import UniversityApply from "@/pages/university/UniversityApply";
import UniversityStatus from "@/pages/university/UniversityStatus";
import UniversityPaymentReturn from "@/pages/university/UniversityPaymentReturn";
import UniversityApplicationSubmitted from "@/pages/university/UniversityApplicationSubmitted";
import UniversityQuiz from "@/pages/university/quiz/UniversityQuiz";
import UniversityRecommendation from "@/pages/university/quiz/UniversityRecommendation";
import UniversityProgramDetail from "@/pages/university/quiz/UniversityProgramDetail";
import UniversityHerbalWaitlist from "@/pages/university/quiz/UniversityHerbalWaitlist";
import AdminUniversity from "@/pages/university/AdminUniversity";
import AdminUniversityCatalog from "@/pages/university/AdminUniversityCatalog";
import UniversityEnroll from "@/pages/university/UniversityEnroll";
import UniversityMyEnrollments from "@/pages/university/UniversityMyEnrollments";
import UniversityStudentPortal from "@/pages/university/UniversityStudentPortal";
import AdminUniversityEnrollments from "@/pages/university/AdminUniversityEnrollments";
import UniversityLesson from "@/pages/university/UniversityLesson";
import UniversityQuizPage from "@/pages/university/UniversityQuizPage";
import UniversityCourse from "@/pages/university/UniversityCourse";
import UniversityCECalendar from "@/pages/university/UniversityCECalendar";
import UniversityResidency from "@/pages/university/UniversityResidency";
import UniversityEventDetail from "@/pages/university/UniversityEventDetail";
import UniversityEventReturn from "@/pages/university/UniversityEventReturn";
import AdminUniversityEvents from "@/pages/university/AdminUniversityEvents";
import AdminUniversityLMS from "@/pages/university/AdminUniversityLMS";
import AdminUniversityGrading from "@/pages/university/AdminUniversityGrading";
import UniversityAssignment from "@/pages/university/UniversityAssignment";
import UniversityFinalExam from "@/pages/university/UniversityFinalExam";
import AdminUniversityExams from "@/pages/university/AdminUniversityExams";
import Register from "@/pages/Register";
import Subscribe from "@/pages/Subscribe";
import SubscribeNew from "@/pages/SubscribeNew";
import ActivateAccount from "@/pages/ActivateAccount";
import EcosystemHome from "@/pages/EcosystemHome";
// Public marketing site (Phase 2C)
import PublicHome from "@/pages/public/Home";
import PublicHairScalp from "@/pages/public/HairScalp";
import PublicFunctionalWellness from "@/pages/public/FunctionalWellness";
import PublicPrograms from "@/pages/public/Programs";
import PublicInClinicServices from "@/pages/public/InClinicServices";
import PublicAbout from "@/pages/public/About";
import PublicStartHere from "@/pages/public/StartHere";
import PublicNavigator from "@/pages/public/WellnessNavigator";
import PublicShop from "@/pages/public/Shop";
import PublicProductDetail from "@/pages/public/ProductDetail";
import PublicCart from "@/pages/public/Cart";
import PublicShopOrderReturn from "@/pages/public/ShopOrderReturn";
import ClinicShop from "@/pages/admin/ClinicShop";
import ClinicWebsite from "@/pages/admin/ClinicWebsite";
import ClinicOffers from "@/pages/admin/ClinicOffers";
import ClinicPrograms from "@/pages/admin/ClinicPrograms";
import ClientPrograms from "@/pages/ClientPrograms";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminUniversityBulkUpload from "@/pages/university/AdminUniversityBulkUpload";
import AdminUniversityTranscripts from "@/pages/university/AdminUniversityTranscripts";
import UniversityCommunity from "@/pages/university/UniversityCommunity";
import PlaceholderPage from "@/pages/PlaceholderPage";
import AdminExecutive from "@/pages/AdminExecutive";
import AdminInterests from "@/pages/AdminInterests";
import PractitionerBilling from "@/pages/PractitionerBilling";

// ─── Placeholder factories ─── (polished "in development" pages for routes referenced
// from navigation / specs but not yet built. Better than 404 and discoverable for SEO.)
const PH = {
  coachingLanding: () => <PlaceholderPage testId="ph-coaching" doorLabel="Coaching & Consulting" title="Coaching & Consulting"
    purpose="Six signature programs supporting identity, healing, and aligned ambition." back="/"
    expectedFunction={["Program catalog (The Me I See, Healing What You Cannot Feel, She Is, Aligned Empire, +2)", "Discovery call booking", "Coaching contract + intake", "Cohort-based group sessions"]}
    contactEmail="c.von@kissedbycvon.com" status="Coming Soon" />,
  coachingProgram: () => <PlaceholderPage doorLabel="Coaching Program" title="Coaching Program"
    purpose="Detail page for an individual coaching program." back="/coaching"
    expectedFunction={["Program overview + outcomes", "Schedule + cohort dates", "Investment + payment options", "Apply / book a discovery call"]} contactEmail="c.von@kissedbycvon.com" />,
  comfortCare: () => <PlaceholderPage testId="ph-comfort-care" doorLabel="Comfort Care" title="KCIS Comfort Care"
    purpose="In-home holistic comfort and wellness services for chronic illness, palliative & hospice support, and recovery care." back="/"
    expectedFunction={["Referral intake form (clinician + family)", "Service catalog (companion, comfort, recovery)", "Family portal with progress notes", "Service-area + scheduling"]} contactEmail="c.von@kissedbycvon.com" status="Coming Soon" />,
  comfortCareSub: ({ title, purpose }) => <PlaceholderPage doorLabel="Comfort Care" title={title} purpose={purpose} back="/comfort-care" contactEmail="c.von@kissedbycvon.com" />,
  community: () => <PlaceholderPage testId="ph-community" doorLabel="KCIS Community" title="KCIS Community"
    purpose="The broader KCIS community space. (The University community is already live at /university/community.)" back="/"
    expectedFunction={["Sis community for graduates + clients", "Practitioner community", "Alumni community", "Cross-door peer networking"]}
    contactEmail="c.von@kissedbycvon.com" />,
  shop: () => <PlaceholderPage testId="ph-shop" doorLabel="Shop / Infusions" title="KCIS Shop & Infusions"
    purpose="Wholesale and retail infusions, custom formulations, scalp & wellness products." back="/"
    expectedFunction={["Product catalog with practitioner pricing tiers", "Custom infusion ordering", "Practitioner wholesale memberships", "Subscription delivery"]}
    contactEmail="c.von@kissedbycvon.com" status="Coming Soon" />,
  adminCoaching: () => <PlaceholderPage testId="ph-admin-coaching" doorLabel="Admin · Coaching" title="Coaching Admin"
    purpose="Coaching cohort + client management surface." back="/admin/dashboard"
    expectedFunction={["Active cohort list", "Per-client coaching notes", "Program scheduling", "Revenue + retention reports"]} />,
  adminComfortCare: () => <PlaceholderPage testId="ph-admin-comfort-care" doorLabel="Admin · Comfort Care" title="Comfort Care Admin"
    purpose="Manage referrals, services, and family-portal access." back="/admin/dashboard"
    expectedFunction={["Referral inbox", "Service catalog editor", "Family-portal user management", "Recovery / comfort notes"]} />,
  adminCommunity: () => <PlaceholderPage testId="ph-admin-community" doorLabel="Admin · Community" title="Cross-Door Community Admin"
    purpose="Moderate the KCIS-wide community. (University community moderation is already live via API + /admin/university/community.)" back="/admin/dashboard"
    expectedFunction={["Sis/Alumni/Practitioner feed moderation", "Bulk pin/lock/remove", "Toxicity AI flags", "Engagement reports"]} />,
  adminShop: () => <PlaceholderPage testId="ph-admin-shop" doorLabel="Admin · Shop" title="Shop & Infusions Admin"
    purpose="Product catalog + inventory + wholesale tiers." back="/admin/dashboard"
    expectedFunction={["Catalog editor", "Inventory & lot tracking", "Wholesale price tiers", "Subscription management"]} />,
  practitionerBilling: () => <PlaceholderPage testId="ph-practitioner-billing" doorLabel="Practitioner Portal" title="Practitioner Subscription Billing"
    purpose="Manage your KCIS Practitioner subscription, payment method, and invoices." back="/practitioner"
    expectedFunction={["Active plan & renewal date", "Update payment method (Square Subscriptions)", "Download invoices", "Cancel / upgrade"]} status="Coming Soon" contactEmail="c.von@kissedbycvon.com" />,
};
import UniversityAlumniDirectory from "@/pages/university/UniversityAlumniDirectory";
import UniversityRegistry from "@/pages/university/UniversityRegistry";
import AdminHub from "@/pages/AdminHub";
import AdminKuhasDashboard from "@/pages/AdminKuhasDashboard";
import AdminKuhasPrograms from "@/pages/AdminKuhasPrograms";
import AdminKuhasResidency from "@/pages/AdminKuhasResidency";
import AdminKuhasCertificates from "@/pages/AdminKuhasCertificates";
import PractitionerReferrals from "@/pages/PractitionerReferrals";
import HealthZ from "@/pages/HealthZ";
import UniversityStudentLogin from "@/pages/university/UniversityStudentLogin";
import UniversityBoard from "@/pages/university/UniversityBoard";
import IdentityLanding from "@/pages/identity/IdentityLanding";
import IdentityAssessment from "@/pages/identity/IdentityAssessment";
import IdentityResults from "@/pages/identity/IdentityResults";
import AdminIdentity from "@/pages/identity/AdminIdentity";
import AdminIntelligence from "@/pages/intelligence/AdminIntelligence";
import ClinicalAssessment from "@/pages/assessment/ClinicalAssessment";
import AssessmentPurchased from "@/pages/assessment/AssessmentPurchased";
import EcosystemPortal from "@/pages/EcosystemPortal";
import HostnameRouter from "@/HostnameRouter";
import RequireAdmin from "@/components/RequireAdmin";
import { ScrollToTop } from "@/components/ScrollToTop";
import RequirePractitioner from "@/components/RequirePractitioner";
import AdminUniversityAlumni from "@/pages/university/AdminUniversityAlumni";
import AdminUniversityBoard from "@/pages/university/AdminUniversityBoard";
import AdminUniversityBilling from "@/pages/university/AdminUniversityBilling";
import AdminClinic from "@/pages/clinic/AdminClinic";
import AdminClinicProtocols from "@/pages/clinic/AdminClinicProtocols";
import AdminCoaching from "@/pages/coaching/AdminCoaching";
import AdminComfortCare from "@/pages/comfort_care/AdminComfortCare";
import AdminCommunity from "@/pages/community/AdminCommunity";
import CoachingProgramPublic from "@/pages/coaching/CoachingProgramPublic";
import CoachingLanding from "@/pages/coaching/CoachingLanding";
import CoachingClientLogin from "@/pages/coaching/CoachingClientLogin";
import CoachingLinearLogin from "@/pages/coaching/CoachingLinearLogin";
import CoachingDashboard from "@/pages/coaching/CoachingDashboard";
import MeISeeApp from "@/pages/coaching/MeISeeApp";
import HWYCFApp from "@/pages/coaching/HWYCFApp";
import SheIsApp from "@/pages/coaching/SheIsApp";
import TrilogyCertificate from "@/pages/coaching/TrilogyCertificate";
import CoachingIntake from "@/pages/coaching/CoachingIntake";
import CoachingCheckoutReturn from "@/pages/coaching/CoachingCheckoutReturn";
import AdminKuhas from "@/pages/kuhas/AdminKuhas";
import KuhasStudentView from "@/pages/kuhas/KuhasStudentView";
import BridgeMeISee from "@/pages/coaching/BridgeMeISee";
import AdminMeISee from "@/pages/coaching/AdminMeISee";
// TheMeISeeSalesPage retired 2026-02-15 — the-me-i-see now renders through
// CoachingProgramPublic (single DB-driven page for all three programs).

// Check In with Dr. C — consumer companion app (connect.christyvonta.com)
import CheckInWelcome from "@/pages/checkin/CheckInWelcome";
import CheckInRegister from "@/pages/checkin/CheckInRegister";
import CheckInSignIn from "@/pages/checkin/CheckInSignIn";
import CheckInOnboarding from "@/pages/checkin/CheckInOnboarding";
import CheckInHome from "@/pages/checkin/CheckInHome";
import CheckInConversation from "@/pages/checkin/CheckInConversation";
import CheckInPlans from "@/pages/checkin/CheckInPlans";
import CheckInCheckoutReturn from "@/pages/checkin/CheckInCheckoutReturn";
import CheckInPolicies from "@/pages/checkin/CheckInPolicies";
import CheckInAdultsOnly from "@/pages/checkin/CheckInAdultsOnly";
import CheckInAdmin from "@/pages/checkin/admin/CheckInAdmin";
import CheckInAdminSignIn from "@/pages/checkin/admin/CheckInAdminSignIn";
import CheckInOperations from "@/pages/checkin/admin/CheckInOperations";
import ConversationIntelligence from "@/pages/checkin/admin/ConversationIntelligence";
import ConversationOverview from "@/pages/checkin/admin/ConversationOverview";
import CheckInHistory from "@/pages/checkin/CheckInHistory";
// Phase 1.5 — Trust · Identity · Relationship
import CheckInVerifyPhone from "@/pages/checkin/CheckInVerifyPhone";
import CheckInProfileExtras from "@/pages/checkin/CheckInProfileExtras";
import CheckInJourney from "@/pages/checkin/CheckInJourney";

// Phase 7A — KCIS Super Admin Dashboard + KAPE Course Builder + Documentation Center
import KcisAdminShell from "@/pages/kcis_admin/KcisAdminShell";
import KapeCourseBuilder from "@/pages/kcis_admin/KapeCourseBuilder";
import KapeCourseEditor from "@/pages/kcis_admin/KapeCourseEditor";
import DocumentationCenter from "@/pages/kcis_admin/DocumentationCenter";
import InstructorProfiles from "@/pages/kcis_admin/InstructorProfiles";
import AuthorDashboard from "@/pages/kcis_admin/AuthorDashboard";
import ContentStudio from "@/pages/kcis_admin/ContentStudio";
import JobCenter from "@/pages/kcis_admin/JobCenter";
import LegacyNarrationMigration from "@/pages/kcis_admin/LegacyNarrationMigration";
import KapePreview from "@/pages/kape_preview/KapePreview";
import InstructorAssetsLibrary from "@/pages/kcis_admin/InstructorAssetsLibrary";
import EducationalPhilosophy from "@/pages/kcis_admin/EducationalPhilosophy";
// KAPE AI Learning Experience Builder (feature-flagged)
import AILearningExperienceBuilder from "@/pages/kcis_admin/AILearningExperienceBuilder";
// Phase C — Curriculum Planner
import CurriculumExecutiveDashboard from "@/pages/curriculum/ExecutiveDashboard";
import CurriculumProgramList from "@/pages/curriculum/ProgramList";
import CurriculumProgramEditor from "@/pages/curriculum/ProgramEditor";
// Phase 7C — Platform Console (restricted technical administration)
import PlatformConsole from "@/pages/PlatformConsole";

function App() {
  return (
    <div className="App min-h-screen kcis-bg-soft">
      <BrowserRouter>
        <ScrollToTop />
        <HostnameRouter />
        <CartProvider>
        <Routes>
          <Route path="/healthz" element={<HealthZ />} />
          <Route path="/" element={<PublicHome />} />
          <Route path="/hair-scalp" element={<PublicHairScalp />} />
          <Route path="/wellness" element={<PublicFunctionalWellness />} />
          <Route path="/programs" element={<PublicPrograms />} />
          <Route path="/in-clinic-services" element={<PublicInClinicServices />} />
          <Route path="/about" element={<PublicAbout />} />
          <Route path="/start-here" element={<PublicStartHere />} />
          <Route path="/navigator" element={<PublicNavigator />} />
          <Route path="/shop" element={<PublicShop />} />
          <Route path="/shop/product/:slug" element={<PublicProductDetail />} />
          <Route path="/cart" element={<PublicCart />} />
          <Route path="/shop/order/return" element={<PublicShopOrderReturn />} />
          <Route path="/admin/shop" element={<RequireAdmin><ClinicShop /></RequireAdmin>} />
          <Route path="/admin/website" element={<RequireAdmin><ClinicWebsite /></RequireAdmin>} />
          <Route path="/ecosystem" element={<EcosystemHome />} />
          {/* Check In with Dr. C — connect.christyvonta.com */}
          <Route path="/checkin" element={<CheckInWelcome />} />
          <Route path="/checkin/register" element={<CheckInRegister />} />
          <Route path="/checkin/signin" element={<CheckInSignIn />} />
          <Route path="/checkin/onboarding" element={<CheckInOnboarding />} />
          <Route path="/checkin/home" element={<CheckInHome />} />
          <Route path="/checkin/conversation" element={<CheckInConversation />} />
          <Route path="/checkin/plans" element={<CheckInPlans />} />
          <Route path="/checkin/checkout/return" element={<CheckInCheckoutReturn />} />
          <Route path="/checkin/policies" element={<CheckInPolicies />} />
          <Route path="/checkin/adults-only" element={<CheckInAdultsOnly />} />
          <Route path="/checkin/admin" element={<CheckInAdmin />} />
          <Route path="/checkin/admin/operations" element={<CheckInOperations />} />
          <Route path="/checkin/admin/conversations" element={<ConversationIntelligence />} />
          <Route path="/checkin/admin/conversations/:id" element={<ConversationOverview />} />
          <Route path="/checkin/admin/signin" element={<CheckInAdminSignIn />} />
          <Route path="/checkin/history" element={<CheckInHistory />} />
          {/* Phase 1.5 */}
          <Route path="/checkin/verify" element={<CheckInVerifyPhone />} />
          <Route path="/checkin/profile-extras" element={<CheckInProfileExtras />} />
          <Route path="/checkin/journey" element={<CheckInJourney />} />

          {/* KCIS Super Admin Dashboard (Phase 7A) */}
          <Route path="/kcis/admin" element={<KcisAdminShell />} />
          <Route path="/kcis/admin/author-workspace" element={<AuthorDashboard />} />
          <Route path="/kcis/admin/content-studio" element={<ContentStudio />} />
          <Route path="/kcis/admin/instructor-profiles" element={<InstructorProfiles />} />
          <Route path="/kcis/admin/job-center" element={<JobCenter />} />
          <Route path="/kcis/admin/legacy-narration-migration" element={<LegacyNarrationMigration />} />
          <Route path="/kape/preview" element={<KapePreview />} />
          <Route path="/kcis/admin/kape/instructor-assets" element={<InstructorAssetsLibrary />} />
          <Route path="/kcis/admin/university/educational-philosophy" element={<EducationalPhilosophy />} />
          {/* Phase C — Curriculum Planner */}
          <Route path="/kcis/admin/university/curriculum-planner" element={<CurriculumExecutiveDashboard />} />
          <Route path="/kcis/admin/university/curriculum-planner/programs" element={<CurriculumProgramList />} />
          <Route path="/kcis/admin/university/curriculum-planner/programs/:id" element={<CurriculumProgramEditor />} />
          <Route path="/kcis/admin/university/curriculum-planner/competencies" element={<CurriculumProgramList />} />
          <Route path="/kcis/admin/university/course-builder" element={<KapeCourseBuilder />} />
          <Route path="/kcis/admin/university/course-builder/:id" element={<KapeCourseEditor />} />
          <Route path="/kcis/admin/kape/ai-builder" element={<AILearningExperienceBuilder />} />
          <Route path="/kcis/admin/documentation" element={<DocumentationCenter />} />

          {/* Phase 7C — Platform Console (restricted technical administration) */}
          <Route path="/admin/platform" element={<PlatformConsole />} />

          {/* Ecosystem Portal — Phase-2 design preview (will become root on kissedbycvon.com once signed off). */}
          <Route path="/portal" element={<EcosystemPortal />} />
          <Route path="/start" element={<Start />} />
          <Route path="/intake" element={<Intake />} />
          <Route path="/report/:clientId" element={<Report />} />
          <Route path="/client/login" element={<ClientLogin />} />
          <Route path="/client/dashboard" element={<ClientDashboard />} />
          <Route path="/client/followup/:clientId" element={<Followup />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
          <Route path="/admin/accept-invite" element={<AdminAcceptInvite />} />
          {/* Post-rename: /admin is the Choose-a-Door hub, /admin/clinical is the clinical workspace */}
          <Route path="/admin" element={<RequireAdmin><AdminHub /></RequireAdmin>} />
          <Route path="/admin/clinical" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
          {/* Back-compat: /admin/dashboard was the old hub URL; keep as a redirect for inbound links / bookmarks */}
          <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/practitioners" element={<RequireAdmin superOnly><AdminPractitioners /></RequireAdmin>} />
          <Route path="/practitioner" element={<RequirePractitioner><PractitionerDashboard /></RequirePractitioner>} />
          <Route path="/admin/pro/:clientId" element={<RequireAdmin><AdminProAssessment /></RequireAdmin>} />
          <Route path="/admin/pro/:clientId/:assessmentId" element={<RequireAdmin><AdminProAssessment /></RequireAdmin>} />
          <Route path="/wellness-assessment" element={<WellnessAssessment />} />
          <Route path="/assessment" element={<Assessment />} />
          {/* KCIS Assessment Monetization Layer — single tier-aware route + Square return page. */}
          <Route path="/clinical-assessment" element={<ClinicalAssessment />} />
          <Route path="/assessment-purchased" element={<AssessmentPurchased />} />
          {/* Clinic Door — Phase-1 aliases. Same components, new URLs. Legacy URLs above kept for inbound links. */}
          <Route path="/clinic" element={<Landing />} />
          <Route path="/clinic/wellness-assessment" element={<WellnessAssessment />} />
          <Route path="/clinic/deep-dive" element={<ClinicalAssessment />} />
          <Route path="/clinic/deep-dive-purchased" element={<AssessmentPurchased />} />
          <Route path="/admin/wellness-gateway-leads" element={<RequireAdmin><AdminWellnessGatewayLeads /></RequireAdmin>} />
          <Route path="/admin/funnel-inquiries" element={<RequireAdmin><AdminFunnelInquiries /></RequireAdmin>} />
          <Route path="/admin/kcis-adaptive-sessions" element={<RequireAdmin><AdminKcisAdaptiveSessions /></RequireAdmin>} />
          <Route path="/admin/kcis-adaptive-reporting" element={<RequireAdmin><AdminKcisAdaptiveReporting /></RequireAdmin>} />
          <Route path="/admin/kcis-make-operations" element={<RequireAdmin><AdminKcisMakeOperations /></RequireAdmin>} />
          <Route path="/funnels/university" element={<FunnelUniversity />} />
          <Route path="/funnels/coaching" element={<FunnelCoaching />} />
          <Route path="/funnels/kcis-clinical-assessment" element={<FunnelKcisClinical />} />
          <Route path="/funnels/comfort-care" element={<FunnelComfortCare />} />
          {/* Module 4 — KISSED University Admissions */}
          <Route path="/university" element={<UniversityLanding />} />
          <Route path="/university/about" element={<UniversityAbout />} />
          <Route path="/university/college/:collegeKey" element={<UniversityCollege />} />
          <Route path="/university/quiz" element={<UniversityQuiz />} />
          <Route path="/university/recommendation" element={<UniversityRecommendation />} />
          <Route path="/university/program/:key" element={<UniversityProgramDetail />} />
          <Route path="/university/herbal-waitlist" element={<UniversityHerbalWaitlist />} />
          <Route path="/university/apply" element={<UniversityApply />} />
          <Route path="/university/application-submitted" element={<UniversityApplicationSubmitted />} />
          <Route path="/university/status" element={<UniversityStatus />} />
          <Route path="/university/payment/return" element={<UniversityPaymentReturn />} />
          <Route path="/university/payment/cancel" element={<UniversityPaymentReturn />} />
          <Route path="/university/enroll" element={<UniversityEnroll />} />
          <Route path="/university/my-enrollments" element={<UniversityMyEnrollments />} />
          <Route path="/university/student/login" element={<UniversityStudentLogin />} />
          <Route path="/university/student" element={<UniversityStudentPortal />} />
          <Route path="/university/courses/:enrollmentId/:courseCode" element={<UniversityCourse />} />
          <Route path="/university/lessons/:lessonId" element={<UniversityLesson />} />
          <Route path="/university/quizzes/:quizId" element={<UniversityQuizPage />} />
          <Route path="/university/assignments/:assignmentId" element={<UniversityAssignment />} />
          <Route path="/university/final-exam/:enrollmentId" element={<UniversityFinalExam />} />
          <Route path="/university/community" element={<UniversityCommunity />} />
          <Route path="/university/ce-calendar" element={<UniversityCECalendar />} />
          <Route path="/university/residency" element={<UniversityResidency />} />
          <Route path="/university/alumni" element={<UniversityAlumniDirectory />} />
          <Route path="/university/registry" element={<UniversityRegistry />} />
          <Route path="/university/board" element={<UniversityBoard />} />
          <Route path="/university/events/:eventId" element={<UniversityEventDetail />} />
          <Route path="/university/events/return" element={<UniversityEventReturn />} />
          <Route path="/admin/university" element={<RequireAdmin><AdminUniversity /></RequireAdmin>} />
          <Route path="/admin/kuhas/dashboard" element={<RequireAdmin superOnly><AdminKuhasDashboard /></RequireAdmin>} />
          <Route path="/admin/kuhas/programs" element={<RequireAdmin superOnly><AdminKuhasPrograms /></RequireAdmin>} />
          <Route path="/admin/kuhas/residency" element={<RequireAdmin superOnly><AdminKuhasResidency /></RequireAdmin>} />
          <Route path="/admin/kuhas/certificates" element={<RequireAdmin superOnly><AdminKuhasCertificates /></RequireAdmin>} />
          <Route path="/practitioner/referrals" element={<RequirePractitioner><PractitionerReferrals /></RequirePractitioner>} />
          <Route path="/admin/university/catalog" element={<RequireAdmin><AdminUniversityCatalog /></RequireAdmin>} />
          <Route path="/admin/university/enrollments" element={<RequireAdmin><AdminUniversityEnrollments /></RequireAdmin>} />
          <Route path="/admin/university/lms" element={<RequireAdmin><AdminUniversityLMS /></RequireAdmin>} />
          <Route path="/admin/university/grading" element={<RequireAdmin><AdminUniversityGrading /></RequireAdmin>} />
          <Route path="/admin/university/exams" element={<RequireAdmin><AdminUniversityExams /></RequireAdmin>} />
          <Route path="/admin/university/exams/bulk-upload" element={<RequireAdmin><AdminUniversityBulkUpload /></RequireAdmin>} />
          <Route path="/admin/university/transcripts" element={<RequireAdmin><AdminUniversityTranscripts /></RequireAdmin>} />
          <Route path="/register" element={<Register />} />
          <Route path="/subscribe" element={<Subscribe />} />
          <Route path="/subscribe/new" element={<SubscribeNew />} />
          <Route path="/activate-account" element={<ActivateAccount />} />
          <Route path="/admin/subscriptions" element={<RequireAdmin><AdminSubscriptions /></RequireAdmin>} />
          <Route path="/admin/university/ce" element={<RequireAdmin><AdminUniversityEvents /></RequireAdmin>} />
          <Route path="/admin/university/residency" element={<RequireAdmin><AdminUniversityEvents /></RequireAdmin>} />
          {/* Module 1 — Identity & Wellness Alignment Assessment */}
          {/* NEW canonical Identity home under /coaching/identity/* */}
          <Route path="/coaching/identity" element={<IdentityLanding />} />
          <Route path="/coaching/identity/assessment" element={<IdentityAssessment />} />
          <Route path="/coaching/identity/results" element={<IdentityResults />} />
          {/* Legacy /identity/* routes — permanent redirect to /coaching/identity/* */}
          <Route path="/identity" element={<Navigate to="/coaching/identity" replace />} />
          <Route path="/identity/assessment" element={<Navigate to="/coaching/identity/assessment" replace />} />
          <Route path="/identity/results" element={<Navigate to="/coaching/identity/results" replace />} />
          <Route path="/admin/identity" element={<RequireAdmin superOnly><AdminIdentity /></RequireAdmin>} />
          {/* KCIS Intelligence Layer (Phase 1) */}
          <Route path="/admin/intelligence" element={<RequireAdmin superOnly><AdminIntelligence /></RequireAdmin>} />
          <Route path="/admin/executive" element={<RequireAdmin superOnly><AdminExecutive /></RequireAdmin>} />
          <Route path="/admin/interests" element={<RequireAdmin><AdminInterests /></RequireAdmin>} />
          {/* ─── Placeholder routes for in-development doors / admin surfaces ─── */}
          {/* Coaching door */}
          <Route path="/coaching" element={<CoachingLanding />} />
          <Route path="/coaching/programs" element={<CoachingLanding />} />
          <Route path="/coaching/dashboard" element={<CoachingDashboard />} />
          <Route path="/coaching/login" element={<CoachingClientLogin />} />
          <Route path="/coaching/me-i-see" element={<MeISeeApp />} />
          <Route path="/coaching/bridge/me-i-see-to-hwycf" element={<BridgeMeISee />} />
          <Route path="/coaching/hwycf/login" element={<CoachingLinearLogin programSlug="healing-what-you-cannot-feel" />} />
          <Route path="/coaching/healing-what-you-cannot-feel/app" element={<HWYCFApp />} />
          <Route path="/coaching/she-is/login" element={<CoachingLinearLogin programSlug="she-is" />} />
          <Route path="/coaching/she-is/app" element={<SheIsApp />} />
          <Route path="/coaching/trilogy/certificate" element={<TrilogyCertificate />} />
          <Route path="/admin/coaching/me-i-see" element={<RequireAdmin><AdminMeISee /></RequireAdmin>} />
          <Route path="/admin/coaching" element={<RequireAdmin><AdminCoaching /></RequireAdmin>} />
          <Route path="/admin/coaching/clients" element={<RequireAdmin><AdminCoaching /></RequireAdmin>} />
          <Route path="/admin/coaching/sessions" element={<RequireAdmin><AdminCoaching /></RequireAdmin>} />
          <Route path="/admin/coaching/programs" element={<RequireAdmin><AdminCoaching /></RequireAdmin>} />
          <Route path="/admin/coaching/enrollments" element={<RequireAdmin><AdminCoaching /></RequireAdmin>} />
          <Route path="/admin/kuhas" element={<RequireAdmin><AdminKuhas /></RequireAdmin>} />
          <Route path="/kuhas/student" element={<KuhasStudentView />} />
          <Route path="/admin/coaching/identity" element={<RequireAdmin><AdminCoaching /></RequireAdmin>} />
          {/* Comfort Care door */}
          <Route path="/comfort-care" element={<PH.comfortCare />} />
          <Route path="/comfort-care/referral" element={<PH.comfortCareSub title="Comfort Care Referral" purpose="Submit a referral for a loved one or client requiring in-home holistic comfort care." />} />
          <Route path="/comfort-care/services" element={<PH.comfortCareSub title="Comfort Care Services" purpose="Service catalog and offerings." />} />
          <Route path="/comfort-care/contact" element={<PH.comfortCareSub title="Contact Comfort Care" purpose="Reach the Comfort Care intake team." />} />
          <Route path="/comfort-care/family-portal" element={<PH.comfortCareSub title="Family Portal" purpose="Secure portal for family members to track care progress and communicate." />} />
          <Route path="/admin/comfort-care" element={<RequireAdmin><AdminComfortCare /></RequireAdmin>} />
          <Route path="/admin/comfort-care/referrals" element={<RequireAdmin><AdminComfortCare /></RequireAdmin>} />
          <Route path="/admin/comfort-care/clients" element={<RequireAdmin><AdminComfortCare /></RequireAdmin>} />
          {/* KCIS-wide community */}
          <Route path="/community" element={<PH.community />} />
          <Route path="/community/sis" element={<PH.community />} />
          <Route path="/community/alumni" element={<UniversityAlumniDirectory />} />
          <Route path="/community/practitioners" element={<PH.community />} />
          <Route path="/admin/community" element={<RequireAdmin><AdminCommunity /></RequireAdmin>} />
          {/* Public coaching program pages — DB-backed (only published render) */}
          <Route path="/coaching/:slug/enroll" element={<CoachingIntake />} />
          <Route path="/coaching/:slug/enroll/return" element={<CoachingCheckoutReturn />} />
          <Route path="/coaching/:slug" element={<CoachingProgramPublic />} />
          <Route path="/admin/university/community" element={<RequireAdmin><AdminCommunity /></RequireAdmin>} />
          {/* Shop / Infusions */}
          <Route path="/shop" element={<PH.shop />} />
          <Route path="/infusions" element={<PH.shop />} />
          <Route path="/infusions/products" element={<PH.shop />} />
          <Route path="/infusions/memberships" element={<PH.shop />} />
          <Route path="/tea" element={<PH.shop />} />
          <Route path="/admin/shop" element={<RequireAdmin><PH.adminShop /></RequireAdmin>} />
          <Route path="/admin/infusions" element={<RequireAdmin><PH.adminShop /></RequireAdmin>} />
          {/* Practitioner subscription billing */}
          <Route path="/practitioner/billing" element={<PractitionerBilling />} />
          <Route path="/practitioner/dashboard" element={<PractitionerDashboard />} />
          <Route path="/practitioner/clients" element={<PractitionerDashboard />} />
          <Route path="/practitioner/login" element={<PractitionerDashboard />} />
          {/* Admin University sub-pages */}
          <Route path="/admin/university/alumni" element={<RequireAdmin><AdminUniversityAlumni /></RequireAdmin>} />
          <Route path="/admin/university/board" element={<RequireAdmin><AdminUniversityBoard /></RequireAdmin>} />
          <Route path="/admin/university/billing" element={<RequireAdmin><AdminUniversityBilling /></RequireAdmin>} />
          {/* Clinic admin (new) */}
          <Route path="/admin/clinic" element={<RequireAdmin><AdminClinic /></RequireAdmin>} />
          <Route path="/admin/offers" element={<RequireAdmin><ClinicOffers /></RequireAdmin>} />
          <Route path="/admin/programs" element={<RequireAdmin><ClinicPrograms /></RequireAdmin>} />
          <Route path="/client/programs" element={<ClientPrograms />} />
          <Route path="/admin/clinic/protocols" element={<RequireAdmin><AdminClinicProtocols /></RequireAdmin>} />
          {/* Generic /login fallback (for users who type it directly) */}
          <Route path="/login" element={<AdminLogin />} />
        </Routes>
        </CartProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
